import { concat, isArray, isEqual, keys, mergeWith, uniq } from 'lodash';
import * as XLSX from 'xlsx';
import { momentTimezone } from './components/utils/dateUtils';

export const isEmpty = (val: any) => val == null || val === '';

export const mergeWithArray = (object: any, other: any) => {
  const customizer = (objValue: any, srcValue: any) => {
    if (isArray(objValue)) {
      return objValue.concat(srcValue);
    }
  };

  return mergeWith(object, other, customizer);
};

export const getRandomVibrantColor = (): string => {
  const hue = Math.floor(Math.random() * 360);
  const saturation = Math.floor(Math.random() * 30) + 70; // Saturation between 70% and 100%
  const lightness = Math.floor(Math.random() * 20) + 50; // Lightness between 50% and 70%
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export const getValue = (el: string, attr: string) => {
  const element: any = document.querySelector(el);
  const nb =
    element &&
    Number(window.getComputedStyle(element)[attr as any]?.replace('px', ''));

  return /top|bottom/.test(attr)
    ? element?.getBoundingClientRect()[attr]
    : isNaN(nb)
      ? window.getComputedStyle(element)[attr as any]
      : nb;
};

export const getHeight = (
  elId: string,
  elAttr: string,
  mainEl: string,
  attribute: string,
) => {
  const value1 = getValue(elId, elAttr);
  const value2 = getValue(mainEl, attribute);

  return value1 + value2;
};

export const getRandom = (array: any[]) =>
  array[Math.floor(Math.random() * array.length)];

export const getReadableColor = (hexColor?: string) => {
  if (!hexColor) return 'black';

  // Remove the leading hash if present
  hexColor = hexColor.replace(/^#/, '');

  // Parse the hex color
  const bigint = parseInt(hexColor, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  // Calculate the brightness of the color
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // Return 'black' for light colors and 'white' for dark colors
  return brightness > 128 ? 'black' : 'white';
};

export const formatDate = (dateStr: string, us?: boolean, format?: string) => {
  const date = momentTimezone(`${dateStr}`);

  return date.isValid()
    ? date.format(format || (us ? 'YYYY-MM-DD' : 'DD/MM/YYYY'))
    : '-';
};

export const formatPhoneNumber = (phoneNumber: string, spaces?: boolean) => {
  // Supprimer tous les caractères non numériques
  let cleaned = ('' + phoneNumber).replace(/\D/g, '');

  // Vérifier si le numéro commence par '33' ou '+33' et le remplacer par '0'
  if (cleaned.startsWith('33')) {
    cleaned = '0' + cleaned.slice(2);
  } else if (cleaned.startsWith('+33')) {
    cleaned = '0' + cleaned.slice(3);
  } else if (cleaned.length === 9 && !cleaned.startsWith('0')) {
    // Ajouter un '0' au début si le numéro est court et ne commence pas par '0'
    cleaned = '0' + cleaned;
  }

  // Formater le numéro en groupes de deux chiffres
  const formatted = cleaned.replace(/(\d{2})(?=\d)/g, '$1 ').trim();

  return spaces ? formatted : cleaned;
};

export const strNomalize = (str: string = '') => {
  if (str == null) return '';

  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/ |'/g, '_')
    .replace(/\./g, '')
    .toLowerCase();
};

export const exportToExcel = (jsonData: any, filename = 'export_doublons') => {
  const workBook = XLSX.utils.book_new();
  const workSheet = XLSX.utils.json_to_sheet(jsonData);

  XLSX.utils.book_append_sheet(workBook, workSheet, '');
  XLSX.writeFile(workBook, `${filename}.xlsx`);
};

export const addressFormat = (addr: any) =>
  `${addr.street || ''}${
    addr.street && (addr.city || addr.postal) ? ', ' : ''
  }${addr.city || ''} ${addr.postal || ''}`.trim();

export const indexOfObject = (
  mainArr: Record<string, any>[],
  key: string,
  searchStr: string | number,
) => {
  return mainArr
    .map((i) => `${i[key]}`.toLowerCase())
    .indexOf(`${searchStr}`.toLowerCase());
};

export const regCompare = (
  pattern: string,
  text: string = '',
  options?: string,
) => {
  const regex = new RegExp(strNomalize(pattern), options);
  return regex.test(strNomalize(text));
};

export const updateFavicon = (newFavicon: string) => {
  const link: any =
    document.querySelector("link[rel*='icon']") ||
    document.createElement('link');
  link.type = 'image/x-icon';
  link.rel = 'shortcut icon';
  link.href = `${newFavicon}.ico`;
  document.head.appendChild(link);

  const title: any = document.querySelector('head title');
  title.textContent = newFavicon.toUpperCase();
};

export const downloadFile = async (
  data: any,
  filename = 'files',
  isUrl = false,
) => {
  try {
    const url = isUrl ? data : window.URL.createObjectURL(new Blob([data]));

    // Create an invisible anchor element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}`; // Set the file name
    document.body.appendChild(link);
    link.click(); // Trigger the download
    link.remove(); // Clean up after the download
  } catch (error) {
    console.error('Error downloading the zip:', error);
  }
};

export const htmlToText = (val: string) => {
  const textArea = document.createElement('div');
  textArea.innerHTML = val;
  return textArea.textContent;
};

export const getDiff = (oldValue: any = {}, newValue: any = {}) => {
  const oldKeys = keys(oldValue);
  const newKeys = keys(newValue);
  const allKeys = uniq(concat(oldKeys, newKeys));

  return allKeys.reduce(
    (prev, key) =>
      /createdBy|__v|rowId|client_id|_id|created_at|updated_at|isDuplicate/.test(
        key,
      ) || isEqual(oldValue[key], newValue[key])
        ? prev
        : {
            ...prev,
            [key]: { old: oldValue[key], new: newValue[key] },
          },
    {},
  );
};

function hexToRgb(hex: string): [number, number, number] {
  hex = hex.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return [r, g, b];
}

// --- Conversion HSL
function hexToHsl(hex: string): [number, number, number] {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0,
    s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0,
    g = 0,
    b = 0;

  if (h >= 0 && h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function generateBalancedColors(baseHex: string): {
  background: string;
  text: string;
} {
  const [h, s, l] = hexToHsl(baseHex);

  let bgL = l;
  let textL = l;

  if (l >= 70) {
    // Trop clair → texte beaucoup plus sombre
    bgL = l;
    textL = Math.max(l - 50, 15);
  } else if (l <= 35) {
    // Trop foncé → on éclaircit bg ET texte
    bgL = Math.min(l + 40, 90);
    textL = Math.max(bgL - 30, 20);
  } else {
    // Normal → contraste standard
    bgL = Math.min(l + 20, 90);
    textL = Math.max(l - 35, 20);
  }

  const bgHex = hslToHex(h, s, bgL);
  const textHex = hslToHex(h, s, textL);

  return { background: bgHex, text: textHex };
}

export const getContrastedColor = (baseHex: string, diff?: number) => {
  const [h, s, l] = hexToHsl(baseHex);
  return l > 50
    ? hslToHex(h, s, Math.max(l - (diff || 20), 15))
    : hslToHex(h, s, Math.min(l + (diff || 20), 90));
};
